// A visit to the Capital as a "trip": ledger, home watch, home-raid risk, and settlement.
//
// THE RULE: the scribe never lies. Every entry is built from something the game actually did, with the
// real numbers filled in — a raid that really started, a building that really burned, coin that really
// moves. If a number isn't known, the sentence doesn't claim it.
//
//  • While away, capital gains/losses collect in the ledger as PENDING; they are applied to the money
//    score once, when the hero comes home (endTrip), and the player is shown the tally.
//  • Home events are watched, not invented: raids, torched buildings/plots, cats lost, hunger.
//  • While away there is a real chance of a real raid (after a grace period). It emits Events.RaidStart,
//    and the existing goon simulation does the rest.

import { Events, on, emit } from '../ui/eventBus.js'
import { TripConfig } from '../config/capital.js'
import { BuildingConfig, RaidDamageConfig, WorkModes } from '../config/balance.js'
import * as season from './seasonManager.js'
import { getBuildings } from './buildingState.js'
import { damageCount } from './damageManager.js'
import { getHungerState } from './feedManager.js'
import { formatCoins } from '../ui/format.js'

let trip = null
let riskTimer = null
let raidEndTimer = null // lives outside `trip`: a raid you rushed home to fight still ends on schedule
let wired = false
let nextId = 1
let lastKilledMsgAt = 0

export const isOnTrip = () => trip !== null
export const getTrip = () => trip

export function startTrip(place = 'Capital City') {
  if (trip) return trip
  wire()
  trip = {
    place,
    startedAt: Date.now(),
    startWealth: season.getWealth(),
    entries: [],
    raid: { active: false, count: 0, lastAt: 0, wave: 0, announced: false },
    counters: { buildings: 0, plots: 0, catsLost: 0, injured: 0, converted: 0, starved: 0 },
  }
  clearInterval(riskTimer)
  riskTimer = setInterval(riskTick, 1000)
  record({ kind: 'info', text: `You left for ${place}. Home is on its own.`, source: 'trip' })
  return trip
}

// ── Ledger ─────────────────────────────────────────────────────────────────
// kind: 'gain' | 'loss' (capital coin, PENDING until you're home) | 'home' (something happened at home) | 'info'
// severity (optional) escalates it to an on-screen alert: whisper < smoke < drums < banner.
export function record({ kind, amount = 0, text, source, severity = null }) {
  if (!trip) return null
  const e = { id: nextId++, t: Date.now(), kind, amount: Math.abs(Math.round(amount)), text, source, severity }
  trip.entries.push(e)
  emit(Events.TripLedgerChange, { entry: e })
  if (severity) emit(Events.TripAlert, { entry: e })
  return e
}
export const credit = (amount, text, source, severity = null) => record({ kind: 'gain', amount, text, source, severity })
export const debit = (amount, text, source, severity = null) => record({ kind: 'loss', amount, text, source, severity })

export function pendingNet() {
  if (!trip) return 0
  return trip.entries.reduce((s, e) => s + (e.kind === 'gain' ? e.amount : e.kind === 'loss' ? -e.amount : 0), 0)
}

// Live, COMPUTED status of the homestead (nothing canned).
export function homeStatus() {
  const sites = damageCount()
  return {
    raidActive: !!trip?.raid.active,
    sites,
    workMode: season.getWorkMode(),
    hunger: getHungerState(),
    treasuryDelta: trip ? season.getWealth() - trip.startWealth : 0,
  }
}

// ── Home watch (real events only) ───────────────────────────────────────────
function posture() {
  return season.getWorkMode() === WorkModes.VILLAGE
    ? 'your workers were guarding the village'
    : 'your workers were out in the fields'
}
const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`

function damageSeverity() {
  const total = trip.counters.buildings + trip.counters.plots
  return total === 1 ? 'smoke' : total % 3 === 0 ? 'banner' : null
}

function wire() {
  if (wired) return
  wired = true

  on(Events.RaidStart, (p) => {
    if (!trip || p?.source !== 'capital') return
    // (startRaid() has already marked the raid active and zeroed the wave counter BEFORE emitting, so the
    // opening wave — spawned synchronously by the goon simulation — is counted.)
    // The first wave's size is known by now; wait a beat anyway so the alert quotes the final number.
    setTimeout(() => {
      if (!trip || trip.raid.announced) return
      trip.raid.announced = true
      const n = trip.raid.wave
      record({
        kind: 'home', source: 'raid', severity: 'drums',
        text: n > 0
          ? `Raiders have crossed the field line — ${plural(n, 'goon', 'goons')} so far!`
          : 'A raid has begun at home!',
      })
    }, 120)
  })
  on(Events.RaidWave, (p) => {
    if (!trip || !trip.raid.active) return
    trip.raid.wave += p.count
    if (trip.raid.announced) {
      record({ kind: 'home', source: 'raid', text: `Reinforcements: ${plural(p.count, 'more goon', 'more goons')} (${p.alive} at large).` })
    }
  })
  on(Events.RaidEnd, (p) => {
    if (!trip || p?.source !== 'capital') return
    trip.raid.active = false
    const sites = damageCount()
    record({
      kind: 'home', source: 'raid', severity: sites > 0 ? 'smoke' : 'whisper',
      text: sites > 0
        ? `The raid is over. ${plural(sites, 'site lies', 'sites lie')} damaged — repairs would cost ${formatCoins(sites * RaidDamageConfig.paidRepairCostPerDamage)}.`
        : 'The raid is over. Nothing was burned.',
    })
  })
  on(Events.BuildingDamaged, ({ tileIndex }) => {
    if (!trip) return
    const b = getBuildings().find((x) => x.tileIndex === tileIndex)
    const name = (b && BuildingConfig[b.type]?.name) || 'building'
    trip.counters.buildings += 1
    record({ kind: 'home', source: 'damage', severity: damageSeverity(), text: `Your ${name} was torched — ${posture()}.` })
  })
  on(Events.PlotDamaged, () => {
    if (!trip) return
    trip.counters.plots += 1
    record({ kind: 'home', source: 'damage', severity: damageSeverity(), text: `A rice paddy was scorched — ${posture()}.` })
  })
  on(Events.WildGoonKilled, () => {
    if (!trip) return
    trip.counters.catsLost += 1
    const now = Date.now()
    const loud = now - lastKilledMsgAt > 10_000
    lastKilledMsgAt = now
    record({ kind: 'home', source: 'goon', severity: loud ? 'smoke' : null, text: 'A goon struck down one of your cats.' })
  })
  on(Events.CitizenInjured, () => {
    if (!trip) return
    trip.counters.injured += 1
    record({ kind: 'home', source: 'goon', text: 'Your citizens drove off a goon but were injured.' })
  })
  on(Events.WildGoonConverted, () => {
    if (!trip) return
    trip.counters.converted += 1
    record({ kind: 'home', source: 'temple', text: 'A goon entered your Temple and now serves you.' })
  })
  on(Events.CatHungerChange, ({ state }) => {
    if (!trip) return
    if (state === 'skinny') record({ kind: 'home', source: 'hunger', severity: 'whisper', text: 'Your citizens are going hungry. Feeding resets the clock.' })
    if (state === 'dying') record({ kind: 'home', source: 'hunger', severity: 'banner', text: 'Your citizens are starving — cats may die if no one feeds them.' })
  })
  on(Events.CatStarvationDeath, () => {
    if (!trip) return
    trip.counters.starved += 1
    record({ kind: 'home', source: 'hunger', severity: 'banner', text: 'A cat starved at home.' })
  })
  // The game's own random events (rolled at season changes) keep happening while you're away.
  on(Events.EventTriggered, (p) => {
    if (!trip || p?.restored) return
    if (p.wealthDelta < 0) {
      const pct = p.wealthBefore > 0 ? Math.round((-p.wealthDelta / p.wealthBefore) * 100) : 0
      record({ kind: 'home', source: 'event', severity: 'smoke', text: `${p.label} struck the homestead and took ${formatCoins(-p.wealthDelta)} (${pct}% of your treasury).` })
    } else {
      const mins = p.durationMs >= 60_000 ? `${Math.round(p.durationMs / 60_000)} min` : `${Math.round(p.durationMs / 1000)} s`
      record({ kind: 'home', source: 'event', severity: 'whisper', text: `${p.label} at home — ${p.desc}${p.durationMs ? ` (${mins})` : ''}` })
    }
  })
  on(Events.CapitalPoiFound, ({ poi }) => {
    if (!trip) return
    record({ kind: 'info', source: 'explore', text: `You discovered the ${poi.name} ${poi.cn}.` })
  })
  on(Events.CapitalLeadsOpened, ({ names, gated }) => {
    if (!trip || !names?.length) return
    const list = names.length === 1 ? `the ${names[0]}` : `the ${names.slice(0, -1).join(', the ')} and the ${names[names.length - 1]}`
    record({ kind: 'info', source: 'explore', text: gated
      ? `The palace guards have admitted you — faint rings on the ground now mark ${list}.`
      : `Faint rings on the ground now point the way to ${list}.` })
  })
  on(Events.WorkModeChange, ({ mode }) => {
    if (!trip) return
    record({ kind: 'info', source: 'order', text: mode === WorkModes.VILLAGE ? 'You ordered the workers to guard the village.' : 'You ordered the workers back to the fields.' })
  })
}

// ── Home-raid risk (absence) ─────────────────────────────────────────────────
// Current raid chance per minute (0 while in the grace period or if no more raids are allowed).
export function currentRiskPerMin() {
  if (!trip) return 0
  const away = Date.now() - trip.startedAt
  if (away < TripConfig.graceMs || trip.raid.count >= TripConfig.maxRaids) return 0
  const extraMin = Math.max(0, (away - TripConfig.graceMs) / 60_000)
  let perMin = Math.min(TripConfig.riskCapPerMin, TripConfig.riskBasePerMin + TripConfig.riskGrowPerMin * extraMin)
  if (season.getWorkMode() === WorkModes.VILLAGE) perMin *= TripConfig.villageMult
  return perMin
}
export const graceLeftMs = () => (trip ? Math.max(0, TripConfig.graceMs - (Date.now() - trip.startedAt)) : 0)

function riskTick() {
  if (!trip || trip.raid.active) return
  const away = Date.now() - trip.startedAt
  if (away < TripConfig.graceMs) return
  if (trip.raid.count >= TripConfig.maxRaids) return
  if (trip.raid.lastAt && Date.now() - trip.raid.lastAt < TripConfig.raidCooldownMs) return
  const perMin = currentRiskPerMin()
  if (Math.random() < perMin / 60) startRaid()
}

export function startRaid() {
  if (!trip || trip.raid.active) return false
  trip.raid.lastAt = Date.now()
  trip.raid.active = true
  trip.raid.count += 1
  trip.raid.wave = 0
  trip.raid.announced = false
  emit(Events.RaidStart, { source: 'capital' }) // ← the REAL raid machinery takes it from here
  clearTimeout(raidEndTimer)
  raidEndTimer = setTimeout(() => emit(Events.RaidEnd, { source: 'capital' }), TripConfig.raidDurationMs)
  return true
}

// ── Settlement (on return) ───────────────────────────────────────────────────
export function endTrip() {
  if (!trip) return null
  clearInterval(riskTimer)
  const t = trip
  const wealthBefore = season.getWealth()
  const gain = t.entries.reduce((s, e) => s + (e.kind === 'gain' ? e.amount : 0), 0)
  const lossAsked = t.entries.reduce((s, e) => s + (e.kind === 'loss' ? e.amount : 0), 0)
  if (gain > 0) season.earnWealth(gain)
  const lost = lossAsked > 0 ? season.fineWealth(lossAsked) : 0 // can't take more than you have
  const sites = damageCount()
  const summary = {
    place: t.place, startedAt: t.startedAt, endedAt: Date.now(), durationMs: Date.now() - t.startedAt,
    startWealth: t.startWealth,
    homeDelta: wealthBefore - t.startWealth, // what home income/expenses did to the treasury while you were away
    wealthBefore, wealthAfter: season.getWealth(),
    gain, loss: lost, lossAsked, net: gain - lost,
    sites, repairCost: sites * RaidDamageConfig.paidRepairCostPerDamage,
    raids: t.raid.count, raidActive: t.raid.active,
    counters: { ...t.counters },
    entries: t.entries.slice(),
  }
  trip = null
  emit(Events.TripLedgerChange, {})
  return summary
}

if (import.meta.env?.DEV) {
  window.__trip = { startTrip, endTrip, record, credit, debit, startRaid, get: () => trip, cfg: TripConfig, homeStatus, pendingNet, emit, Events }
}
